import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  NgApexchartsModule
} from 'ng-apexcharts';

import {MatCardModule} from '@angular/material/card';
import {AssistantMetrics} from "./assistant-metrics.model";

@Component({
  selector: 'app-assistant-report',
  standalone: true,
    imports: [MatCardModule, NgApexchartsModule],
  templateUrl: './assistant-report.component.html'
})
export class AssistantReportComponent implements OnChanges {
  private _metricsData: AssistantMetrics[] = [];

  @Input()
  set metricsData(value: AssistantMetrics[] | null | undefined) {
    this._metricsData = value ?? [];
  }

  get metricsData(): AssistantMetrics[] {
    return this._metricsData;
  }

  // --- Summary KPIs ---
  // totalMessageRoutedToAssistant: messages sent to assistant
  // totalAssistantResponded: successful responses
  // totalErrors: errors encountered
  summary = {
    totalRouted: 0,
    totalResponded: 0,
    totalErrors: 0,
    avgResponse: 0 // in seconds
  };

  // --- Timeline Chart (Avg Response Time Over Time) ---
  timelineSeries: ApexAxisChartSeries = [];
  timelineChart!: ApexChart;
  timelineXAxis!: ApexXAxis;
  timelineStroke!: ApexStroke;
  timelineTooltip!: ApexTooltip;

  // --- Issues Donut Chart (Errors vs. Successful Responses) ---
  issuesSeries: ApexNonAxisChartSeries = [];
  issuesChart!: ApexChart;
  issuesLabels: string[] = [];
  issuesTooltip!: ApexTooltip;

  // --- Usage Stacked Bar Chart (Daily Totals) ---
  usageSeries: ApexAxisChartSeries = [];
  usageChart!: ApexChart;
  usageXAxis!: ApexXAxis;
  usagePlotOptions!: ApexPlotOptions;
  usageDataLabels!: ApexDataLabels;
  usageLegend!: ApexLegend;
  usageResponsive!: ApexResponsive[];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['metricsData'] && this.metricsData.length) {
      this.computeSummary();
      this.buildTimelineChart();
      this.buildIssuesChart();
      this.buildUsageChart();
    }
  }

  private computeSummary(): void {
    const totals = this.metricsData.reduce(
        (acc, cur) => {
          acc.totalRouted += cur.totalMessageRoutedToAssistant;
          acc.totalResponded += cur.totalAssistantResponded;
          acc.totalErrors += cur.totalErrors;
          // Use totalAssistantResponded as weight for average response time.
          if (cur.totalAssistantResponded > 0) {
            acc.responseTimeSum += cur.avgTimeTakenToRespond * cur.totalAssistantResponded;
            acc.responseCount += cur.totalAssistantResponded;
          }
          return acc;
        },
        {
          totalRouted: 0,
          totalResponded: 0,
          totalErrors: 0,
          responseTimeSum: 0,
          responseCount: 0
        }
    );

    this.summary.totalRouted = totals.totalRouted;
    this.summary.totalResponded = totals.totalResponded;
    this.summary.totalErrors = totals.totalErrors;
    // Convert ms to seconds (rounded, no decimals).
    this.summary.avgResponse =
        totals.responseCount > 0
            ? Math.round(totals.responseTimeSum / totals.responseCount / 1000)
            : 0;
  }

  private buildTimelineChart(): void {
    // Group records by date (YYYY-MM-DD) to compute a weighted average response time.
    const grouped: { [date: string]: { sum: number; count: number } } = {};
    this.metricsData.forEach(metric => {
      const date = metric.reportDate.split('T')[0];
      if (!grouped[date]) {
        grouped[date] = { sum: 0, count: 0 };
      }
      if (metric.totalAssistantResponded > 0) {
        grouped[date].sum += metric.avgTimeTakenToRespond * metric.totalAssistantResponded;
        grouped[date].count += metric.totalAssistantResponded;
      }
    });
    const dates = Object.keys(grouped).sort();
    const seriesData = dates.map(date =>
        grouped[date].count > 0
            ? Math.round(grouped[date].sum / grouped[date].count / 1000)
            : 0
    );

    this.timelineSeries = [
      { name: 'Avg Response (s)', data: seriesData }
    ];
    this.timelineChart = {
      type: 'line',
      height: 350,
      toolbar: { show: true }
    };
    this.timelineXAxis = {
      categories: dates,
      title: { text: 'Date' }
    };
    this.timelineStroke = { curve: 'smooth', width: 3 };
    this.timelineTooltip = {
      y: { formatter: (val: number) => `${val} s` }
    };
  }

  private buildIssuesChart(): void {
    // For the issues chart, show errors vs. successful responses.
    const totalResponded = this.metricsData.reduce((sum, m) => sum + m.totalAssistantResponded, 0);
    const totalErrors = this.metricsData.reduce((sum, m) => sum + m.totalErrors, 0);
    const successful = totalResponded - totalErrors;

    this.issuesSeries = [totalErrors, successful];
    this.issuesLabels = ['Errors', 'Successful Responses'];
    this.issuesChart = {
      type: 'donut',
      height: 350
    };
    this.issuesTooltip = {
      y: { formatter: (val: number) => val.toString() }
    };
  }

  private buildUsageChart(): void {
    // Group by report date to aggregate daily usage:
    // - Routed messages,
    // - Assistant responses,
    // - Errors.
    const grouped: {
      [date: string]: {
        routed: number;
        responded: number;
        errors: number;
      };
    } = {};
    this.metricsData.forEach(metric => {
      const date = metric.reportDate.split('T')[0];
      if (!grouped[date]) {
        grouped[date] = { routed: 0, responded: 0, errors: 0 };
      }
      grouped[date].routed += metric.totalMessageRoutedToAssistant;
      grouped[date].responded += metric.totalAssistantResponded;
      grouped[date].errors += metric.totalErrors;
    });

    const dates = Object.keys(grouped).sort();
    const routedSeries = dates.map(date => grouped[date].routed);
    const respondedSeries = dates.map(date => grouped[date].responded);
    const errorsSeries = dates.map(date => grouped[date].errors);

    this.usageSeries = [
      { name: 'Routed', data: routedSeries },
      { name: 'Responses', data: respondedSeries },
      { name: 'Errors', data: errorsSeries }
    ];
    this.usageChart = {
      type: 'bar',
      height: 350,
      stacked: true,
      toolbar: { show: true }
    };
    this.usageXAxis = {
      categories: dates,
      title: { text: 'Date' }
    };
    this.usagePlotOptions = {
      bar: { horizontal: false, columnWidth: '35%', borderRadius: 4 }
    };
    this.usageDataLabels = { enabled: false };
    this.usageLegend = { show: true };
    this.usageResponsive = [
      {
        breakpoint: 600,
        options: { plotOptions: { bar: { borderRadius: 3 } } }
      }
    ];
  }
}
